import { useState } from 'react';
import { Lock, Check, AlertCircle } from './icons';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export function ChangePasswordSection() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!user) {
      setError('You must be logged in to change your password.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      // Verify current password by re-authenticating
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: currentPassword,
      });
      if (signInError) {
        setError('Your current password is incorrect.');
        setSaving(false);
        return;
      }

      // Update to the new password
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(false), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="freud-card-elevated p-6">
      <div className="flex items-center gap-3 mb-1">
        <div className="p-2.5 bg-sage-100 rounded-xl flex-shrink-0">
          <Lock className="w-5 h-5 text-sage-600" />
        </div>
        <div>
          <h2 className="text-heading-xs text-gray-80">Change Password</h2>
          <p className="text-text-sm text-gray-40">
            Choose a new password for your account. You'll use it the next time you sign in.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-4 mt-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 bg-sage-100 border border-sage-200 text-sage-700 px-4 py-3 rounded-xl mb-4 mt-4">
          <Check className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">Password updated successfully.</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
            Current Password
          </label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="freud-input w-full"
            placeholder="Enter your current password"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
            New Password
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            autoComplete="new-password"
            className="freud-input w-full"
            placeholder="At least 8 characters"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
            Confirm New Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            className="freud-input w-full"
            placeholder="Re-enter your new password"
          />
        </div>

        <button
          type="submit"
          disabled={saving || !currentPassword || !newPassword || !confirmPassword}
          className="freud-btn-primary px-6 py-3 rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}
